import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Filter, Grid as GridIcon, List, Eye, Edit, 
  Trash2, Plus, X, CheckCircle, AlertCircle, Package,
  TrendingUp, BarChart3, PieChart, Activity, Users,
  DollarSign, Clock, HelpCircle, FileText, Wallet,
  Send, CreditCard, Award, Layout as LayoutIcon
} from 'lucide-react';
import api from '../../../services/api';
import LoadingSpinner from '../../../components/LoadingSpinner';
import UniversalSearchInput from '../../../components/common/UniversalSearchInput';

const WidgetLibrary = () => {
  const [widgets, setWidgets] = useState([]);
  const [filteredWidgets, setFilteredWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedWidget, setSelectedWidget] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const widgetTypes = [
    { value: 'all', label: 'All Widgets', icon: Package },
    { value: 'kpi', label: 'KPI Widgets', icon: TrendingUp },
    { value: 'chart', label: 'Chart Widgets', icon: BarChart3 },
    { value: 'table', label: 'Table Widgets', icon: List },
    { value: 'action', label: 'Action Widgets', icon: Activity },
  ];

  useEffect(() => {
    loadWidgets();
  }, []);

  useEffect(() => {
    filterWidgets();
  }, [searchQuery, selectedType, widgets]);

  const loadWidgets = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/dashboard/widget-types');
      setWidgets(response.data || []);
    } catch (error) {
      console.error('Failed to load widgets:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterWidgets = () => {
    let filtered = widgets;

    // Filter by type
    if (selectedType !== 'all') {
      filtered = filtered.filter(w => w.widget_type === selectedType);
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(w => 
        w.widget_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        w.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        w.widget_code.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredWidgets(filtered);
  };

  const getWidgetIcon = (iconName) => {
    const iconMap = {
      Users, Send, Activity, DollarSign, Wallet, AlertCircle,
      Clock, HelpCircle, CheckCircle, FileText, LayoutIcon,
      TrendingUp, PieChart, CreditCard, Award, BarChart3
    };
    const IconComponent = iconMap[iconName] || Package;
    return IconComponent;
  };

  const getTypeColor = (type) => {
    const colors = {
      kpi: 'bg-blue-100 text-blue-800 border-blue-200',
      chart: 'bg-green-100 text-green-800 border-green-200',
      table: 'bg-purple-100 text-purple-800 border-purple-200',
      action: 'bg-orange-100 text-orange-800 border-orange-200',
      custom: 'bg-gray-100 text-gray-800 border-gray-200',
    };
    return colors[type] || colors.custom;
  };

  const handleViewDetails = (widget) => {
    setSelectedWidget(widget);
    setShowDetails(true);
  };

  const getWidgetStats = () => {
    const stats = {
      total: widgets.length,
      kpi: widgets.filter(w => w.widget_type === 'kpi').length,
      chart: widgets.filter(w => w.widget_type === 'chart').length,
      table: widgets.filter(w => w.widget_type === 'table').length,
      action: widgets.filter(w => w.widget_type === 'action').length,
      active: widgets.filter(w => w.is_active).length,
    };
    return stats;
  };

  const stats = getWidgetStats();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Widget Library</h1>
          <p className="text-sm text-gray-600 mt-1">
            Browse and manage dashboard widgets
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            <Plus className="w-4 h-4" />
            Create Widget
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg border border-gray-200 p-4"
        >
          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          <div className="text-xs text-gray-600 mt-1">Total Widgets</div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-blue-50 rounded-lg border border-blue-200 p-4"
        >
          <div className="text-2xl font-bold text-blue-900">{stats.kpi}</div>
          <div className="text-xs text-blue-700 mt-1">KPI Widgets</div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-green-50 rounded-lg border border-green-200 p-4"
        >
          <div className="text-2xl font-bold text-green-900">{stats.chart}</div>
          <div className="text-xs text-green-700 mt-1">Chart Widgets</div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-purple-50 rounded-lg border border-purple-200 p-4"
        >
          <div className="text-2xl font-bold text-purple-900">{stats.table}</div>
          <div className="text-xs text-purple-700 mt-1">Table Widgets</div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-orange-50 rounded-lg border border-orange-200 p-4"
        >
          <div className="text-2xl font-bold text-orange-900">{stats.action}</div>
          <div className="text-xs text-orange-700 mt-1">Action Widgets</div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-green-50 rounded-lg border border-green-200 p-4"
        >
          <div className="text-2xl font-bold text-green-900">{stats.active}</div>
          <div className="text-xs text-green-700 mt-1">Active</div>
        </motion.div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <UniversalSearchInput
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search widgets by name, code, or description..."
              className="ml-2"
            />
          </div>

          {/* Type Filter */}
          <div className="flex items-center gap-2">
            {widgetTypes.map(type => {
              const IconComponent = type.icon;
              return (
                <button
                  key={type.value}
                  onClick={() => setSelectedType(type.value)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                    selectedType === type.value
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-blue-500'
                  }`}
                >
                  <IconComponent className="w-4 h-4" />
                  <span className="text-sm font-medium hidden md:inline">{type.label}</span>
                </button>
              );
            })}
          </div>

          {/* View Mode Toggle */}
          <div className="flex items-center gap-2 border border-gray-300 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded ${
                viewMode === 'grid' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <GridIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded ${
                viewMode === 'list' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Results count */}
        <div className="mt-3 text-sm text-gray-600">
          Showing {filteredWidgets.length} of {widgets.length} widgets
        </div>
      </div>

      {/* Widget Grid/List */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredWidgets.map((widget, index) => {
            const IconComponent = getWidgetIcon(widget.icon);
            return (
              <motion.div
                key={widget.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-lg transition-all duration-200 cursor-pointer group"
                onClick={() => handleViewDetails(widget)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div 
                    className="p-3 rounded-lg"
                    style={{ backgroundColor: `${widget.default_color}15` }}
                  >
                    <IconComponent 
                      className="w-6 h-6" 
                      style={{ color: widget.default_color }}
                    />
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getTypeColor(widget.widget_type)}`}>
                    {widget.widget_type}
                  </span>
                </div>

                <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                  {widget.widget_name}
                </h3>
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                  {widget.description}
                </p>

                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <code className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
                    {widget.widget_code}
                  </code>
                  <div className="flex items-center gap-1">
                    {widget.is_active ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Widget
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data Source
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredWidgets.map((widget) => {
                const IconComponent = getWidgetIcon(widget.icon);
                return (
                  <tr key={widget.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div 
                          className="p-2 rounded-lg mr-3"
                          style={{ backgroundColor: `${widget.default_color}15` }}
                        >
                          <IconComponent 
                            className="w-5 h-5" 
                            style={{ color: widget.default_color }}
                          />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {widget.widget_name}
                          </div>
                          <div className="text-sm text-gray-500 line-clamp-1">
                            {widget.description}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getTypeColor(widget.widget_type)}`}>
                        {widget.widget_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <code className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded">
                        {widget.widget_code}
                      </code>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {widget.data_source}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {widget.is_active ? (
                        <span className="flex items-center gap-1 text-green-600 text-sm">
                          <CheckCircle className="w-4 h-4" />
                          Active
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-gray-400 text-sm">
                          <AlertCircle className="w-4 h-4" />
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleViewDetails(widget)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="text-gray-600 hover:text-gray-900 mr-3">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button className="text-red-600 hover:text-red-900">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Widget Details Modal */}
      <AnimatePresence>
        {showDetails && selectedWidget && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowDetails(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div 
                      className="p-4 rounded-xl"
                      style={{ backgroundColor: `${selectedWidget.default_color}15` }}
                    >
                      {React.createElement(getWidgetIcon(selectedWidget.icon), {
                        className: "w-8 h-8",
                        style: { color: selectedWidget.default_color }
                      })}
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">
                        {selectedWidget.widget_name}
                      </h2>
                      <span className={`inline-block mt-2 px-3 py-1 text-sm font-medium rounded-full border ${getTypeColor(selectedWidget.widget_type)}`}>
                        {selectedWidget.widget_type}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowDetails(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {/* Details */}
                <div className="space-y-6">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Description</h3>
                    <p className="text-gray-600">{selectedWidget.description}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">Widget Code</h3>
                      <code className="text-sm text-gray-900 bg-gray-100 px-3 py-2 rounded block">
                        {selectedWidget.widget_code}
                      </code>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">Data Source</h3>
                      <code className="text-sm text-gray-900 bg-gray-100 px-3 py-2 rounded block">
                        {selectedWidget.data_source}
                      </code>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">Icon</h3>
                      <p className="text-gray-600">{selectedWidget.icon}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">Default Color</h3>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-6 h-6 rounded border border-gray-300"
                          style={{ backgroundColor: selectedWidget.default_color }}
                        />
                        <code className="text-sm text-gray-600">{selectedWidget.default_color}</code>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Status</h3>
                    <div className="flex items-center gap-2">
                      {selectedWidget.is_active ? (
                        <>
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <span className="text-green-600 font-medium">Active</span>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="w-5 h-5 text-gray-400" />
                          <span className="text-gray-600 font-medium">Inactive</span>
                        </>
                      )}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Timestamps</h3>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p>Created: {new Date(selectedWidget.created_at).toLocaleString()}</p>
                      <p>Updated: {new Date(selectedWidget.updated_at).toLocaleString()}</p>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-6 flex items-center justify-end gap-3 pt-6 border-t border-gray-200">
                  <button
                    onClick={() => setShowDetails(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Close
                  </button>
                  <button className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                    Edit Widget
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Empty State */}
      {filteredWidgets.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No widgets found</h3>
          <p className="text-gray-600 mb-4">
            {searchQuery || selectedType !== 'all' 
              ? 'Try adjusting your filters or search query'
              : 'Get started by creating your first widget'}
          </p>
          {(searchQuery || selectedType !== 'all') && (
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedType('all');
              }}
              className="px-4 py-2 text-blue-600 hover:text-blue-700 font-medium"
            >
              Clear Filters
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default WidgetLibrary;
